/* eslint-disable no-underscore-dangle */
import Notifier from '../Notifier';
import logger from '../logger';

/**
 * This manages the data for the product form, tracking all the variants, the selected variant, and sold out variants
 * This is an Observer than can be subscribed to
 * Subscription types include: 'beforeLoad', 'afterLoad', 'beforeVariantUpdate', 'afterVariantUpdate'
 *
 * This does NOT manage product form submission
 *
 * @namespace
 * @param {Object} variants The product variants to initialize
 * @param {Object} selectedVariant The selected variant. Available from Shopify liquid on the PDP
 * @param {Object} [opts] The options object
 * @param {Integer} opts.primaryOption The primary option, eg. color. If not given options[0] is used.
 *
 * @property {Array} soldOutVariants All variants that are sold out (available = false)
 * @property {Object} selectedVariant The currently selected variant
 * @property {Array} relatedVariants Variants related to the selected variant by the primary option
 * @property {Array} soldOutRelatedVariants Sold out variants related to the selected variant by the primary option
 * */

class VariantSelectHandler extends Notifier {
  constructor(variants, selectedVariant, maxQuantity = null, opts = {}) {
    super();
    this.options = opts;
    this.variants = variants;
    this.soldOutVariants = null;
    this.selectedVariant = selectedVariant;
    this.relatedVariants = null;
    this.soldOutRelatedVariants = null;
    this.maxQuantity = maxQuantity;
    // Bind for safety as these may be passed around and lose context
    this.load = this.load.bind(this);
    this.updateVariantFromOptions = this.updateVariantFromOptions.bind(this);
  }

  /**
   * Determine all the sold out variants - those with available === false
   * Determines associated sold out variants based on the primary selection option
   * @returns {void}
   */
  _getSoldOut() {
    const KEY = 'available';
    const VALUE = false;
    const evaluation = (variant) => variant[KEY] === VALUE;
    if (!Array.isArray(this.variants)) return;
    const soldOut = this.variants.filter((variant) => {
      return evaluation(variant);
    });
    this.soldOutVariants = soldOut;

    const primaryOption = this.selectedVariant.options[0]; // base on option 1
    const variants = this.soldOutVariants.filter((variant) => {
      return variant.options[this.options.primaryOption || 0] === primaryOption;
    });
    this.soldOutRelatedVariants = variants;
  }

  /**
   * Get alike variants
   * @returns {Array} All variants with the same primary option
   */
  _getVariantsByPrimaryOption() {
    const curr = this.selectedVariant;
    const optIndex = this.options.primaryOption || 0;
    const related = this.variants.filter((variant) => {
      return variant.options[optIndex] === curr.options[optIndex];
    });
    this.relatedVariants = related;
  }

  /**
   * Wraps up all the data to send to the subscribers
   * @param {string} type The subscription type
   * @param {Error} [error] If an error is provided, this is passed instead of data
   */
  _handleNotifications(type, error) {
    let payload = {
      variants: this.variants,
      soldOutVariants: this.soldOutVariants,
      selectedVariant: this.selectedVariant,
      soldOutRelatedVariants: this.soldOutRelatedVariants,
      maxQuantity: this.maxQuantity,
    };
    if (error) payload = error;
    this.notify(type, payload);
  }

  /**
   * Populates the sold out products and finds associated sold out products
   * Notifies subscriptions to 'load'
   */
  load() {
    try {
      this._getSoldOut();
      this._getVariantsByPrimaryOption();
      this._handleNotifications('load');
    } catch (e) {
      logger.error(e);
      this._handleNotifications('error', e);
    }
  }

  /**
   * From the changed option index and option value, the new selected variant is found and set
   * Notifies subscribers to 'beforeVariantUpdate' & 'afterVariantUpdate'
   * @param {Integer} changedOptionIndex
   * @param {String} changedOptionValue
   */
  updateVariantFromOptions(changedOptionIndex, changedOptionValue) {
    this._handleNotifications('beforeVariantUpdate');
    const newOptions = this.selectedVariant.options.map((option) => option);
    newOptions[changedOptionIndex] = changedOptionValue;
    const newVariant = this.variants.find((variant) => {
      // options are shallow so we can do just a shallow array comparison
      return (
        variant.options.every((option, i) => {
          return newOptions[i] === option;
        }) && variant.options.length === newOptions.length
      );
    });
    this.selectedVariant = newVariant;
    this._getSoldOut();
    this._getVariantsByPrimaryOption();
    this._handleNotifications('afterVariantUpdate');
  }
}

export default VariantSelectHandler;
